import {
    DragDropEffects,
    ExteriorNodeLabelModel,
    GraphComponent,
    ILabel,
    IListEnumerable,
    IModelItem,
    INode,
    NodeDropInputMode,
    SimpleNode,
    SvgExport
} from '@yfiles/yfiles'

export type DragAndDropPanelItem<T extends IModelItem> = { modelItem: T; tooltip?: string; disabled?:boolean }

/**
 * A drag and drop panel component, from which users can drag the items to a {@link GraphComponent}.
 *
 * The component is mounted into the provided HTML element. It can be populated with nodes,
 * edges, labels, and ports using {@link populatePanel}.
 */
export class DragAndDropPanel {
    /**
     * The desired maximum width of each item. This value is used to decide whether a
     * visualization must be scaled down.
     */
    maxItemWidth = 150
    /**
     * Whether the labels of the DnD node visual should be transferred to the created node or discarded.
     */
    copyNodeLabels = false

    /**
     * Create a new DragAndDropPanel and mount it to the provided div element.
     * @param div The element that will display the palette items.
     */
    constructor(public div: HTMLElement) {}

    clearPanel() {
        this.div.innerHTML = ''
    }

    /**
     * Adds the provided items to this panel.
     */
    populatePanel(items: Iterable<DragAndDropPanelItem<INode>>): void {
        // Convert the nodes into plain visualizations, using a canvas that is only needed while drawing them
        const graphComponent = new GraphComponent()
        try {
            for (const item of items) {
                const visual: HTMLDivElement = this.createNodeVisual(item, graphComponent)
                if (!item.disabled) {
                    this.setStartDragListeners(item.modelItem, visual)
                }else {
                    visual.classList.add('demo-dnd-panel__item--disabled')
                    visual.setAttribute('aria-disabled', 'true')
                }
                this.div.appendChild(visual)
            }
        }finally {
            graphComponent.cleanUp()
        }
    }

    private beginDrag(element: HTMLElement, data: INode): void {
        const dragPreviewElement = createDragPreviewElement(element)
        const dragSource = this.startDrag(element, dragPreviewElement, data)

        // let the GraphComponent handle the preview rendering if possible
        if (dragSource) {
            dragSource.addEventListener('query-continue-drag', (evt) => {
                if (evt.dropTarget === null) {
                    dragPreviewElement.classList.remove('hidden')
                } else {
                    dragPreviewElement.classList.add('hidden')
                }
            })
        }
    }

    private startDrag(element: HTMLElement, dragPreviewElement: HTMLElement, data: INode) {
        return NodeDropInputMode.startDrag(
            element,
            data,
            DragDropEffects.ALL,
            true,
            dragPreviewElement
        )
    }

    /**
     * Creates an element that contains the visualization of the given node.
     * This method is used by populatePanel to create the visualization
     * for each node provided by the factory.
     */
    private createNodeVisual(original: DragAndDropPanelItem<INode>, graphComponent: GraphComponent): HTMLDivElement {
        const graph = graphComponent.graph
        graph.clear()

        const originalNode = original.modelItem
        const node = graph.createNode(originalNode.layout, originalNode.style, originalNode.tag)
        originalNode.labels.forEach((label: ILabel) => {
            graph.addLabel(
                node,
                label.text,
                label.layoutParameter,
                label.style,
                label.preferredSize,
                label.tag
            )
        })
        return this.exportAndWrap(graphComponent, original.tooltip)
    }

    /**
     * Exports and wraps the original visualization in an HTML element.
     */
    private exportAndWrap(graphComponent: GraphComponent, tooltip?: string): HTMLDivElement {
        graphComponent.updateContentBounds(10)
        const exporter = new SvgExport({
            worldBounds: graphComponent.contentBounds
        })

        exporter.scale = exporter.calculateScaleForWidth(
            Math.min(this.maxItemWidth, graphComponent.contentBounds.width)
        )
        const visual = exporter.exportSvg(graphComponent)

        // Firefox does not display the SVG correctly because of the clip - so we remove it.
        visual.removeAttribute('clip-path')

        const div = document.createElement('div')
        div.setAttribute('class', 'demo-dnd-panel__item')
        div.setAttribute('role', 'listitem')
        visual.setAttribute('aria-hidden', 'true')
        div.appendChild(visual)
        if (tooltip) {
            div.title = tooltip
            div.setAttribute('aria-label', tooltip)
        }

        return div
    }

    /**
    * Adds mousedown and pointer down listeners to the given element that starts the drag operation.
    */
    private setStartDragListeners(item: INode, element: HTMLElement): void {
        // the actual drag operation
        const doDragOperation = () => {
            // Otherwise, we just use the node itself and let (hopefully) NodeDropInputMode take over
            const simpleNode = new SimpleNode()
            simpleNode.layout = item.layout
            simpleNode.style = item.style.clone()
            simpleNode.tag = item.tag
            simpleNode.labels = this.copyNodeLabels ? item.labels : IListEnumerable.EMPTY
            if (item.ports.size > 0) {
                simpleNode.ports = IListEnumerable.from(item.ports)
            }
            this.beginDrag(element, simpleNode)
        }

        element.addEventListener('pointerdown', (evt) => {
                if (evt.button !== 0) {
                    return
                }
                doDragOperation()
                evt.preventDefault()
            },
            true
        )
    }
}

// the copy of the palette item that follows the pointer until it is over the canvas, where yFiles shows its own preview.
// yFiles leaves its styling to us, so it gets the class the palette styles use to fade it in and out
function createDragPreviewElement(templateElement: HTMLElement): HTMLElement {
    const dragPreview = templateElement.cloneNode(true) as HTMLElement
    dragPreview.style.margin = '0'
    dragPreview.classList.add('yfiles-drag')
    dragPreview.removeAttribute('role')
    dragPreview.setAttribute('aria-hidden', 'true')
    return dragPreview
}
